import { type SortingState, type VisibilityState } from "@tanstack/react-table";
import { createParser, parseAsString, parseAsArrayOf } from "nuqs";

import { usePersistedQueryStates } from "@/src/hooks/use-persisted-query-states";

const parseAsSortOrder = createParser({
  parse(queryValue) {
    // id:desc follow react data table pattern.
    const [id, desc] = queryValue.split(":");

    return { id, desc: desc === "true" };
  },
  serialize(value) {
    return [value.id, value.desc].join(":");
  },
});

export const parseAsColumnVisibility = createParser<VisibilityState>({
  parse: (value) => {
    if (!value) {
      return {};
    }

    return value.split(",").reduce<VisibilityState>((acc, pair) => {
      const [key, val] = pair.split(":");

      if (!key || !val) {
        return acc;
      }

      acc[key] = val === "visible";

      return acc;
    }, {});
  },
  serialize: (value) => {
    return Object.entries(value)
      .map(([key, visible]) => `${key}:${visible ? "visible" : "hidden"}`)
      .join(",");
  },
});

const dataTableParsers = (defaultColumnVisibility: VisibilityState, defaultSorting: SortingState) => ({
  query: parseAsString.withDefault(""),
  columnVisibility: parseAsColumnVisibility.withDefault(defaultColumnVisibility),
  sorting: parseAsArrayOf(parseAsSortOrder, ",").withDefault(defaultSorting),
});

export const DATA_TABLE_QUERY_URL_KEY = "q";

const dataTableOptions = {
  throttleMs: 50,
  clearOnDefault: true,
  urlKeys: { query: DATA_TABLE_QUERY_URL_KEY, columnVisibility: "columns", sorting: "sort" },
};

export function useDataTableState({
  defaultColumnVisibility = {},
  defaultSorting = [],
}: {
  defaultColumnVisibility?: VisibilityState;
  defaultSorting?: SortingState;
} = {}) {
  const parsers = dataTableParsers(defaultColumnVisibility, defaultSorting);

  return usePersistedQueryStates(parsers, dataTableOptions);
}
